/**
 * Reprodução do áudio dos outros participantes.
 *
 * O SDK do SFU entrega as faixas recebidas, mas não toca nada sozinho: sem um
 * elemento de mídia anexado, a conexão funciona por completo e ninguém ouve
 * ninguém. O indicador de quem está falando continua aceso, porque vem de um
 * evento do servidor e não do áudio em si, o que torna a falha especialmente
 * confusa.
 *
 * Este componente fica montado enquanto durar a chamada, e não junto da tela do
 * canal: quem sai para ler o chat precisa continuar ouvindo a conversa.
 */

import { useEffect, useRef } from 'react'
import type { RemoteTrack } from 'livekit-client'
import { useVoiceStore } from '@/store/voice'
import { usePreferencesStore } from '@/store/preferences'

function RemoteAudio({
  track,
  volume,
  volumeDaPessoa,
  deafened,
  outputDeviceId,
}: {
  track: RemoteTrack
  volume: number
  /** Ajuste individual, de 0 a 200, aplicado por cima do volume geral. */
  volumeDaPessoa: number
  deafened: boolean
  outputDeviceId?: string
}) {
  const ref = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    track.attach(element)
    return () => {
      track.detach(element)
    }
  }, [track])

  useEffect(() => {
    const element = ref.current
    if (!element) return
    // Ensurdecer zera o volume aqui também, e não só no lado do servidor: a
    // faixa pode continuar chegando enquanto a inscrição é cancelada.
    //
    // Os dois ajustes se multiplicam: o geral vale para a sala e o individual
    // corrige quem está muito alto ou muito baixo em relação aos outros.
    const combinado = (volume / 100) * (volumeDaPessoa / 100)
    element.volume = deafened ? 0 : Math.min(1, Math.max(0, combinado))
  }, [volume, volumeDaPessoa, deafened])

  useEffect(() => {
    const element = ref.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null
    if (!element || !outputDeviceId || typeof element.setSinkId !== 'function') return

    // Nem todo navegador permite escolher a saída; falhar aqui apenas mantém o
    // dispositivo padrão do sistema.
    void element.setSinkId(outputDeviceId).catch(() => {})
  }, [outputDeviceId])

  return <audio ref={ref} autoPlay playsInline />
}

export function VoiceAudioOutput() {
  const remotes = useVoiceStore((s) => s.remotes)
  const deafened = useVoiceStore((s) => s.deafened)
  const connected = useVoiceStore((s) => s.room !== null)

  const outputVolume = usePreferencesStore((s) => s.audio.outputVolume)
  const outputDeviceId = usePreferencesStore((s) => s.audio.outputDeviceId)
  const userVolumes = usePreferencesStore((s) => s.userVolumes)
  const volumesDeTela = usePreferencesStore((s) => s.volumesDeTela)

  if (!connected) return null

  return (
    <div aria-hidden="true" className="sr-only">
      {Object.values(remotes).flatMap((media) => {
        /*
          A voz da pessoa e o som da tela dela são regulados separadamente.

          Vinham no mesmo ajuste, e abaixar o jogo de alguém abaixava junto a
          voz de quem estava explicando o que aparecia ali.
        */
        const faixas: { track: RemoteTrack; volume: number }[] = []

        if (media.audioTrack) {
          faixas.push({ track: media.audioTrack, volume: userVolumes[media.identity] ?? 100 })
        }
        if (media.screenAudioTrack) {
          faixas.push({
            track: media.screenAudioTrack,
            volume: volumesDeTela[media.identity] ?? 100,
          })
        }

        return faixas.map(({ track, volume }) => (
          <RemoteAudio
            key={track.sid ?? `${media.identity}-${track.kind}`}
            track={track}
            volume={outputVolume}
            volumeDaPessoa={volume}
            deafened={deafened}
            outputDeviceId={outputDeviceId}
          />
        ))
      })}
    </div>
  )
}
